import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material'
import { colorTableDefault } from '../TemplateDefault'

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: colorTableDefault,
    color: theme.palette.common.white,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '10px 12px',
  },
}))

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}))

/**
 * Tabela padrão do módulo.
 *
 * `columns`: [{ id, label, align, width, element: row => ReactNode }]
 * A paginação é controlada pelo pai (server-side ou client-side).
 */
const DataTable = ({
  columns,
  rows,
  loading = false,
  emptyMessage = 'Nenhum registro encontrado.',
  page = 0,
  rowsPerPage = 10,
  total = 0,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  pagination = true,
  rowKey = (row, index) => index,
  dense = false,
}) => {
  const skeletonRows = Array.from({ length: 6 })

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <StyledTableCell
                key={column.id}
                align={column.align || 'left'}
                style={{ width: column.width }}
              >
                {column.label}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            skeletonRows.map((_, rowIndex) => (
              <StyledTableRow key={`skeleton-${rowIndex}`}>
                {columns.map(column => (
                  <StyledTableCell key={`skeleton-${rowIndex}-${column.id}`}>
                    <Skeleton variant="text" />
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))}

          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!loading &&
            rows.map((row, index) => (
              <StyledTableRow
                key={rowKey(row, index)}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map(column => (
                  <StyledTableCell key={column.id} align={column.align || 'left'}>
                    {column.element(row, index)}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))}
        </TableBody>
      </Table>

      {pagination && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <TablePagination
            component="div"
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
            rowsPerPageOptions={[10, 25, 50, 100]}
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </Box>
      )}
    </TableContainer>
  )
}

export default DataTable
